import { useEffect } from 'react';
import { onContextChanged } from '../magpie/academyAssistantChannel';
import { setAcademyAssistantContext, useAcademyAssistantContextStore } from '../stores/academyAssistantContextStore';

/**
 * Host-side counterpart to `useAcademyAssistantContext`'s broadcast: mirrors
 * incoming video-context snapshots from the same-window Magpie channel into the
 * local module-singleton store the chat container reads. Whichever root hosts
 * the assistant mounts this once, so a consumer in another module instance
 * still lands its context here.
 *
 * The mirror only applies when the local store has no owner. A non-null owner
 * means a writer in *this* module instance already owns the context (the common
 * same-bundle case, where the writer's own broadcast echoes straight back). That
 * writer keeps its store authoritative — applying the echo here would reset its
 * ownership token to null (`setContext` defaults `owner` to `null`), after which
 * the writer's `setTimestamp` guard would treat it as dispossessed and go inert.
 * The unowned case is the one that genuinely needs mirroring: a consumer living
 * in a separate module instance, whose writes never reach this store directly.
 *
 * A secondary content-equality check then skips echoes that already match the
 * store, so a redundant snapshot never triggers a needless subscriber update.
 */
export const useAcademyAssistantContextBridge = () => {
  useEffect(() => onContextChanged(incoming => {
    const state = useAcademyAssistantContextStore.getState();
    if (state.owner !== null) {
      return;
    }
    if (state.context.catalogId === incoming.catalogId && state.context.videoTimestampSeconds === incoming.videoTimestampSeconds && state.context.lessonId === incoming.lessonId && state.context.trackId === incoming.trackId) {
      return;
    }
    setAcademyAssistantContext(incoming);
  }), []);
};